// "Mån-Fre 11-15, Tis-Lör 18-22, Sön Stängt"

import { unique } from "@@/utilities/unique";
import { z } from "zod";
import type { MINUTES } from "./date-time/time-constants";
import type { DailySchedule } from "./weekly-schedules/daily-schedule";
import { dailyScheduleFactory } from "./weekly-schedules/daily-schedule";
import { dailyScheduleTimeRangeFactory } from "./weekly-schedules/daily-schedule-time-range";
import type { WeeklySchedule } from "./weekly-schedules/weekly-schedule";

const weekdayLookup = {
    mån: "mon",
    månd: "mon",
    månda: "mon",
    måndag: "mon",
    tis: "tue",
    tisd: "tue",
    tisda: "tue",
    tisdag: "tue",
    ons: "wed",
    onsd: "wed",
    onsda: "wed",
    onsdag: "wed",
    tor: "thu",
    tors: "thu",
    torsd: "thu",
    torsda: "thu",
    torsdag: "thu",
    fre: "fri",
    fred: "fri",
    freda: "fri",
    fresdag: "fri",
    lör: "sat",
    lörd: "sat",
    lörda: "sat",
    lördag: "sat",
    sön: "sun",
    sönd: "sun",
    sönda: "sun",
    söndag: "sun",
} as { [key: string]: (typeof weekdayShortNames)[number] };

const weekdayShortNames = [
    "mon",
    "tue",
    "wed",
    "thu",
    "fri",
    "sat",
    "sun",
] as const;
const swedishWeekdayShortNameLookup = {
    mon: "mån",
    tue: "tis",
    wed: "ons",
    thu: "tor",
    fri: "fre",
    sat: "lör",
    sun: "sön",
};
const swedishWeekdayFullNameLookup = {
    mon: "måndag",
    tue: "tisdag",
    wed: "onsdag",
    thu: "torsdag",
    fri: "fredag",
    sat: "lördag",
    sun: "söndag",
};
type WeekdayShortName = (typeof weekdayShortNames)[number];
const weekDayShortNameZodSchema = z.enum(weekdayShortNames);
// const getWeekdayShortName = (at: Date) =>
//     format(at, "EEE").toLowerCase() as WeekdayShortName;
const getWeekdaySortNumber =
    (firstDay: WeekdayShortName) => (weekday: WeekdayShortName) => {
        const firstDayIndex = weekdayShortNames.findIndex(
            item => item === firstDay,
        );
        const originalDayIndex = weekdayShortNames.findIndex(
            item => item === weekday,
        );
        let dayIndex = originalDayIndex - firstDayIndex;
        dayIndex = dayIndex < 0 ? dayIndex + 7 : dayIndex;
        return dayIndex;
    };

const parseTime = (time: string) => {
    const [startTime, endTime] = time?.split("-") ?? [];
    const [startHour, startMinute] = startTime?.split(":") ?? [];
    const [endHour, endMinute] = endTime?.split(":") ?? [];
    return {
        startHour,
        startMinute,
        endHour,
        endMinute,
    };
};
const parseDaysAndTime = (input: string): WeeklySchedule => {
    const value = input.trim();
    if (typeof value === "undefined") return emptyWeeklySchedule();
    if (!value.indexOf(" ") && !/^\d{1,2}-\d{1,2}$/.test(value)) {
        return emptyWeeklySchedule();
    }
    const [days, times] = value.indexOf(" ")
        ? value.toLocaleLowerCase().split(" ")
        : ["mån-sön", value];

    if (!days.indexOf("-")) return emptyWeeklySchedule();
    const [startDay, endDay] = days.split("-");
    const { startHour, startMinute, endHour, endMinute } = parseTime(times);

    const startWeekdayShortName =
        weekdayLookup[startDay as keyof typeof weekdayLookup]; // mon
    const endWeekdayShortName =
        weekdayLookup[endDay as keyof typeof weekdayLookup]; // mon
    const startDayIndex = getWeekdaySortNumber("mon")(startWeekdayShortName); // 0 + 0
    const endDayIndex =
        (endDay && getWeekdaySortNumber("mon")(endWeekdayShortName)) ||
        startDayIndex; // 0 + 1

    const timeRanges =
        !startHour || startHour.toLowerCase().trim() === "stängt"
            ? []
            : dailyScheduleTimeRangeFactory(
                  {
                      hour: parseInt(startHour),
                      minute: parseInt(startMinute) ?? 0,
                  },
                  {
                      hour: parseInt(endHour),
                      minute: parseInt(endMinute) ?? 0,
                  },
              );

    const hourRanges =
        timeRanges.map(timeRange =>
            dailyScheduleFactory({
                timeRange,
                orderTimePadding: {
                    startInMinutes: 0 as MINUTES,
                    endInMinutes: 0 as MINUTES,
                },
            }),
        ) || [];

    const result: WeeklySchedule = {
        mon: (0 >= startDayIndex && 0 <= endDayIndex && hourRanges) || [],
        tue: (1 >= startDayIndex && 1 <= endDayIndex && hourRanges) || [],
        wed: (2 >= startDayIndex && 2 <= endDayIndex && hourRanges) || [],
        thu: (3 >= startDayIndex && 3 <= endDayIndex && hourRanges) || [],
        fri: (4 >= startDayIndex && 4 <= endDayIndex && hourRanges) || [],
        sat: (5 >= startDayIndex && 5 <= endDayIndex && hourRanges) || [],
        sun: (6 >= startDayIndex && 6 <= endDayIndex && hourRanges) || [],
    };

    return result;
};

const emptyWeeklySchedule = (): WeeklySchedule => ({
    mon: [],
    tue: [],
    wed: [],
    thu: [],
    fri: [],
    sat: [],
    sun: [],
});

type BusinessHoursWeekSimple = {
    [key in keyof WeeklySchedule]: DailySchedule[];
};
const parseWeeklySchedule = (schedule: string): WeeklySchedule => {
    if (!schedule?.trim()) return emptyWeeklySchedule();
    const items = schedule
        .split(",")
        .map(item => item.trim().toLocaleLowerCase())
        .map(parseDaysAndTime)
        .reduce((agg: BusinessHoursWeekSimple, next) => {
            weekdayShortNames.forEach(name => {
                const nextHours = next[name] as DailySchedule[];
                agg[name] = [...agg[name], ...(nextHours || [])];
            });
            return agg;
        }, emptyWeeklySchedule() as BusinessHoursWeekSimple);

    return items;
};

type TimeString = string;
const deparseWeeklySchedule = (
    schedule: WeeklySchedule | undefined,
): string => {
    if (!schedule) return "";
    if (
        weekdayShortNames.every(name => {
            const daily = schedule[name];
            if (typeof daily === "boolean") return daily === false;
            return !daily?.length;
        })
    ) {
        // If no daily schedule is defined, return empty string
        return "";
    }

    const daysForTime = new Map<TimeString, WeekdayShortName[]>();
    const timesForDay = new Map<WeekdayShortName, TimeString[]>();

    // Parse time strings for each day
    weekdayShortNames.forEach(name => {
        const daily = schedule[name];
        if (!daily) return;
        const result: string[] = [];
        if (daily === true) {
            result.push(`00:00-23:59`);
        } else {
            daily.forEach(dailySchedule => {
                const { timeRange } = dailySchedule;
                const { start, end } = timeRange;
                result.push(`${start}-${end}`);
            });
        }

        result.forEach(time => {
            const currentDays = daysForTime.get(time) ?? [];
            daysForTime.set(time, unique([...currentDays, name]));
            const currentTimes = timesForDay.get(name) ?? [];
            timesForDay.set(name, unique([...currentTimes, time]));
        });
    });

    const used = new Set<string>();
    const segments = weekdayShortNames.map(name => {
        // Go through time by time
        // For every time, check if it is used the next day as well
        // If so, check the next and so on, until no more days are used
        // else just add the time and the current days to the result;
        const times = timesForDay.get(name) ?? [];
        const subSegments = times.map(time => {
            const currentDayIndex = weekdayShortNames.findIndex(
                shortName => shortName === name,
            );
            let lastDay = name;
            if (used.has(`${name}${time}`)) return "";
            for (
                let index = currentDayIndex;
                index < weekdayShortNames.length;
                index++
            ) {
                const indexDay = weekdayShortNames[index];
                const current = timesForDay.get(indexDay) ?? [];
                if (current.includes(time)) {
                    lastDay = indexDay;
                    used.add(`${indexDay}${time}`);
                } else {
                    break;
                }
            }

            const startDay = swedishWeekdayShortNameLookup[name];
            const endDay = swedishWeekdayShortNameLookup[lastDay];

            return name === lastDay
                ? `${startDay} ${time}`
                : `${startDay}-${endDay} ${time}`;
        });

        return subSegments.filter(Boolean).join(", ");
    });
    return segments.filter(Boolean).join(", ").replace(/:00/g, "");
};

const isWeekdayShortName = (input: unknown): input is WeekdayShortName => {
    return (
        typeof input === "string" &&
        weekdayShortNames.includes(input as WeekdayShortName)
    );
};

export {
    deparseWeeklySchedule,
    emptyWeeklySchedule,
    isWeekdayShortName,
    parseDaysAndTime,
    parseWeeklySchedule,
    swedishWeekdayFullNameLookup,
    swedishWeekdayShortNameLookup,
    weekdayLookup,
    weekdayShortNames,
    weekDayShortNameZodSchema,
};
export type { BusinessHoursWeekSimple, WeekdayShortName };
